import { useState } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import type { ObjectiveProposal } from '@shared/types/objective-proposals';
import type { WorkspaceObjective } from '@shared/types/workspace-objectives';

/** One objective as shown: the active one, overlaid by the analyst's pending change if any. */
export interface ObjectiveView {
  key: string;
  current: WorkspaceObjective | null;
  proposal: ObjectiveProposal | null;
}

interface Props {
  view: ObjectiveView;
  owner: string;
  /** The critical result: bigger, first. */
  lead?: boolean;
  busy: boolean;
  /** A lever of a main objective still proposed: its title (the lever is validated after it). */
  waitsFor?: string | null;
  onApply: () => void;
  onDismiss: () => void;
}

const BADGES: Record<ObjectiveProposal['action'], string> = {
  create: 'Proposé',
  update: 'Modification proposée',
  archive: 'Arrêt proposé',
};

const formatDate = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

const weeksLeft = (iso: string): string | null => {
  const days = Math.ceil((Date.parse(`${iso}T00:00:00`) - Date.now()) / 86_400_000);
  if (days < 0) return 'échéance passée';
  return days < 14 ? `dans ${days} jour${days > 1 ? 's' : ''}` : `dans ${Math.round(days / 7)} semaines`;
};

/** The current value, struck through, when the proposal changes it. */
const Before = ({ value, next }: { value: string | null | undefined; next: string | null }) =>
  value && value !== next ? <span className="block text-xs font-normal text-domino-inkSoft line-through">{value}</span> : null;

/** An objective: what it aims at, from where to where, by when, why; how it is counted, folded. */
export const ObjectiveCard = ({ view, owner, lead = false, busy, waitsFor = null, onApply, onDismiss }: Props) => {
  const [open, setOpen] = useState(false);
  const { current, proposal } = view;
  const changing = proposal?.action === 'update' ? current : null;
  const shown = proposal && proposal.action !== 'archive' ? proposal : null;
  const title = shown?.title ?? current?.title ?? '';
  const measure = shown?.measure ?? current?.success_criteria ?? '';
  const target = shown ? shown.target : current?.target ?? null;
  const baseline = shown ? shown.baseline : current?.baseline ?? null;
  const dueDate = shown ? shown.dueDate : current?.due_date ?? null;
  return (
    <article
      className={cn(
        'rounded-xl border bg-domino-warm px-5 py-4',
        proposal ? 'border-domino-aubergine/40' : 'border-domino-cardBorder',
        proposal?.action === 'archive' && 'opacity-60',
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {proposal && <span className="rounded-full bg-domino-aubergine px-2 py-0.5 font-medium text-white">{BADGES[proposal.action]}</span>}
            <span className="text-domino-inkSoft">{owner}</span>
          </div>
          <h3 className={cn('mt-1.5 font-semibold leading-snug text-domino-ink', lead ? 'font-display text-2xl' : 'text-base')}>
            {title}
            <Before value={changing?.title} next={title} />
          </h3>
        </div>
        {proposal && (
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <Button size="sm" onClick={onApply} disabled={busy || Boolean(waitsFor)} title={waitsFor ? `Validez d’abord « ${waitsFor} »` : undefined}>Valider</Button>
            <button type="button" onClick={onDismiss} disabled={busy} className="text-xs text-domino-inkSoft hover:text-domino-ink hover:underline">Écarter</button>
          </div>
        )}
      </div>

      {(baseline || target || dueDate) && (
        <div className={cn('mt-3 flex flex-wrap items-center gap-x-4 gap-y-2', lead ? 'text-base' : 'text-sm')}>
          {(baseline || target) && (
            <span className="inline-flex flex-wrap items-center gap-2 rounded-lg bg-white/80 px-3 py-1.5">
              <span className="text-domino-inkSoft">{baseline ?? 'Départ à mesurer'}</span>
              <ArrowRight className="h-4 w-4 shrink-0 text-domino-aubergine" aria-label="vers" />
              <span className="font-semibold text-domino-ink">{target ?? 'Cible à fixer'}</span>
            </span>
          )}
          {dueDate && (
            <span className="text-domino-inkSoft">
              Échéance <span className="font-medium text-domino-ink">{formatDate(dueDate)}</span>
              {weeksLeft(dueDate) && <span> · {weeksLeft(dueDate)}</span>}
            </span>
          )}
        </div>
      )}
      {changing && (
        <div className="mt-1 text-xs">
          <Before value={changing.baseline} next={baseline} />
          <Before value={changing.target} next={target} />
          <Before value={changing.due_date ? formatDate(changing.due_date) : null} next={dueDate ? formatDate(dueDate) : null} />
        </div>
      )}

      {proposal?.why && <p className="mt-3 text-sm leading-6 text-domino-ink">{proposal.why}</p>}

      {measure && (
        <div className="mt-3 border-t border-domino-cardBorder pt-2">
          <button type="button" aria-expanded={open} onClick={() => setOpen(!open)} className="flex items-center gap-1 text-sm text-domino-inkSoft hover:text-domino-ink">
            <ChevronDown className={cn('h-4 w-4 transition-transform', !open && '-rotate-90')} aria-hidden />
            Comment on compte
          </button>
          {open && (
            <p className="mt-2 text-sm leading-6 text-domino-ink">
              {measure}
              <Before value={changing?.success_criteria} next={measure} />
            </p>
          )}
        </div>
      )}
    </article>
  );
};
